// A game's updates on its page: their kinds and labels, the insight card for
// the one that moved players most, and the list of the latest with what each
// did to the count. Nothing here touches the DOM.

import type { AppResponse, UpdateImpact, UpdateKind } from "../../shared/api.ts";
import { IMPACT_KINDS } from "../../shared/limits.ts";
import { changeText, date, number } from "./figures.ts";
import { escapeHtml, dayChange } from "./web.ts";
import { externalLink, plural } from "./markup.ts";
import { postUrl } from "./routes.ts";

// Update announcements, by the label the developer picked on Steam, strongest
// first. A marker for several updates in one bucket takes the strongest kind.
export const UPDATE_LABELS = {
  major: "Major update",
  regular: "Regular update",
  small: "Small update"
} as const satisfies Record<UpdateKind, string>;

export const UPDATE_KINDS: readonly UpdateKind[] = ["major", "regular", "small"];

// An update kind's label; the kind itself for one this copy doesn't know.
export const updateLabel = (kind: string): string =>
  Object.hasOwn(UPDATE_LABELS, kind) ? UPDATE_LABELS[kind as UpdateKind] : kind;

// An update's title, or a stand-in for one posted without.
export const updateTitle = (update: { title: string }): string => update.title || "Untitled";

// The update kinds as toggle buttons, pressed where `shown` (a Set) has them,
// each tagged with `attr` for app.js to find; `count` adds a slot for how
// many are on the chart.
export const kindToggles = (attr: string, shown: ReadonlySet<string>, count = false): string =>
  UPDATE_KINDS.map(
    (kind) => `<button type="button" class="${kind}" ${attr}="${kind}"
      aria-pressed="${shown.has(kind)}">${kind}${count ? ' <span class="count"></span>' : ""}</button>`
  ).join("");

// The kinds marked and listed until a reader picks their own (toggles.ts keeps
// that per browser). Small updates (patch notes) start hidden: some games post
// several a week. Also the kinds the impact card weighs, as update_bump does.
export const DEFAULT_UPDATE_KINDS = IMPACT_KINDS;

// How many of the latest updates a game page lists, of the kinds shown. The
// API sends this many of each kind (UPDATES_PER_KIND in workers/shared/updates.ts), so the
// newest of any mix are all there.
const UPDATES_LISTED = 5;

// Why an update has no impact, by the API's reason for it.
const NO_IMPACT = {
  untracked: "Not tracked the week before",
  missing_days: "Missing data"
} as const;

// An update with a figure: a change, from the averages either side of it.
type Measured = UpdateImpact & { change: number; before: number; after: number };
const measured = (update: UpdateImpact): update is Measured =>
  update.change != null && update.before != null && update.after != null;

// The recent update that moved its players most, either way: of the latest
// major and regular updates the API sends (small ones are patch notes),
// those with a figure, the largest change. A part week counts, against the
// same weekdays the week before, so a big launch shows as soon as it has a
// day after it. Nothing until one has a figure. Always those two kinds,
// whatever the toggles show, so it's the same card for every reader and in
// the Worker's copy, and its label says so.
export function updateCard(game: AppResponse): string {
  const biggest = (game.updates ?? [])
    .filter((update) => DEFAULT_UPDATE_KINDS.includes(update.kind))
    .filter(measured)
    .reduce<Measured | null>(
      (a, update) => (!a || Math.abs(update.change) > Math.abs(a.change) ? update : a),
      null
    );

  const change = biggest && dayChange(biggest.change);
  if (!biggest || !change) return "";
  const span = biggest.days_in
    ? `${biggest.days_in} ${plural(biggest.days_in, "day")} in, against the same days the week before`
    : "the week after against the week before";

  return `
      <div class="insight update-impact">
        <div class="stat-label">Biggest change after a recent major or regular update</div>
        <div class="insight-value">
          <span class="change ${change.way}">${changeText(change)}</span>
          <small>avg players</small>
        </div>
        <small>${updateLabel(biggest.kind)}, ${date(biggest.t)}:
          ${externalLink(postUrl(game.app_id, biggest), updateTitle(biggest))}</small>
        <small>${number.format(biggest.before)} → ${number.format(biggest.after)},
          ${span}</small>
      </div>`;
}

// What an update did to the count, the API's week after against the week
// before (see latestUpdates in workers/web), with how far into the week a
// part week is; or why there isn't a figure, and for one too new, the day
// its first will be.
function updateImpact(update: UpdateImpact): string {
  const why = (text: string): string => `<span class="count impact"><small>${text}</small></span>`;

  if (update.reason === "too_soon") {
    return why(
      update.ready_at ? `Check back ${date(update.ready_at)}` : "Too soon to tell"
    );
  }
  if (update.reason) return why(NO_IMPACT[update.reason]);

  // Without a reason, the API has a figure (see latestUpdates in ../web).
  const change = dayChange(update.change);
  if (!change || !measured(update)) return why(NO_IMPACT.missing_days);
  const partWeek = update.days_in
    ? ` · ${update.days_in} ${plural(update.days_in, "day")} in`
    : "";

  return `
      <span class="count impact">
        <span class="change ${change.way}">${changeText(change)}</span>
        <small>${number.format(update.before)} → ${number.format(update.after)}
          avg players${partWeek}</small>
      </span>`;
}

// The latest updates of `kinds` (an array or a Set, of UPDATE_LABELS keys),
// newest first, each with what it did to the player count. Nothing for a
// game with no updates found. The list picks its kinds apart from the
// chart's markers, with toggles of its own (render-game.ts wires them up,
// swapping in updateRows).
export function updateList(game: AppResponse, name: string, kinds: Iterable<string>): string {
  if (!game.updates?.length) return "";

  const shown = new Set(kinds);

  return `
      <section class="updates">
        <div class="section-head">
          <div>
            <h2>Recent ${escapeHtml(name)} updates</h2>
            <p class="muted">The change in average players in the week after each
              update, compared with the week before.</p>
          </div>
          <div class="toggles list-toggles" role="group" aria-label="Update kinds listed">
            <span class="muted">Show</span>
            ${kindToggles("data-list-kind", shown)}
          </div>
        </div>
        <div class="update-rows">${updateRows(game, shown)}</div>
      </section>`;
}

// The rows of updateList for the kinds in `shown` (a Set), or why there
// are none, pointing at the list's toggles.
export function updateRows(game: AppResponse, shown: ReadonlySet<string>): string {
  const updates = game.updates
    .filter((update) => shown.has(update.kind))
    .slice(0, UPDATES_LISTED);

  const rows = updates.map(
    (update) => `
        <li>
          <span class="when">${date(update.t)}</span>
          <span class="what">
            <span class="update-kind ${update.kind}">${updateLabel(update.kind)}</span>
            ${externalLink(postUrl(game.app_id, update), updateTitle(update))}
          </span>
          ${updateImpact(update)}
        </li>`
  );

  if (rows.length) return `<ol class="panel update-list">${rows.join("")}</ol>`;

  const kindNames = UPDATE_KINDS.filter((kind) => shown.has(kind)).join(" or ");

  return `<p class="panel status">${
    kindNames
      ? `No ${kindNames} updates found on Steam. Turn on other kinds above to list them.`
      : "Turn on an update kind above to list its updates."
  }</p>`;
}
